import { median } from '../utils/reportFormat';
import { ymdInOrgTimeZone, fourHourBinIndex } from '../utils/organizationalTime';
import { isPatientMessage } from './messageReportService';

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const WITHIN_15_MIN_MS = 15 * MINUTE_MS;

/** Compact labels for the six 4-hour time-of-day bins (org timezone). */
export const HOUR_BIN_LABELS = ['12–4a', '4–8a', '8a–12p', '12–4p', '4–8p', '8p–12a'];

function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Ordered, continuous list of org-timezone calendar days spanning [start, end].
 * Anchored at noon so DST / midnight edges don't shift the resolved date.
 */
function buildDayScaffold(start, end) {
  const days = [];
  if (!start || !end) return days;
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 12, 0, 0, 0);
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 12, 0, 0, 0);
  const seen = new Set();
  while (cursor <= last && days.length <= 400) {
    const key = ymdInOrgTimeZone(cursor);
    if (key && !seen.has(key)) {
      seen.add(key);
      const [, m, d] = key.split('-').map(Number);
      days.push({ key, label: `${m}/${d}` });
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function minutesFromMs(ms) {
  if (ms == null || Number.isNaN(ms)) return 0;
  return Math.round((ms / 60_000) * 10) / 10;
}

function pct(part, whole) {
  return whole ? Math.round((100 * part) / whole) : null;
}

/**
 * Reduce a list of {ms, created} entries into per-day and per-time-of-day
 * medians + counts, aligned to the supplied day scaffold.
 */
function summarize(entries, dayScaffold) {
  const dayArrays = new Map(dayScaffold.map((d) => [d.key, []]));
  const hourArrays = HOUR_BIN_LABELS.map(() => []);
  entries.forEach(({ ms, created }) => {
    const dk = ymdInOrgTimeZone(created);
    if (dayArrays.has(dk)) dayArrays.get(dk).push(ms);
    const hi = fourHourBinIndex(created);
    if (hourArrays[hi]) hourArrays[hi].push(ms);
  });
  const byDay = dayScaffold.map((d) => {
    const arr = dayArrays.get(d.key) || [];
    const med = median(arr);
    return { key: d.key, label: d.label, medianMs: med, medianMin: minutesFromMs(med), count: arr.length };
  });
  const byHour = HOUR_BIN_LABELS.map((label, i) => {
    const arr = hourArrays[i];
    const med = median(arr);
    return { label, medianMs: med, medianMin: minutesFromMs(med), count: arr.length };
  });
  return { byDay, byHour };
}

/**
 * Build the message-acknowledgement response-time section.
 * @param {Array} messages   message docs (createdAt/readAt Date-coerced)
 * @param {Set}   inScopeIds  in-scope conversationIds (optional)
 * @param {Array} dayScaffold continuous day list for the period
 */
function buildMessageSection(messages, inScopeIds, dayScaffold, end) {
  const inScope = (m) => !inScopeIds || inScopeIds.has(m.conversationId);
  const entries = [];
  let unacknowledged = 0;
  (messages || []).forEach((m) => {
    if (!inScope(m) || !isPatientMessage(m)) return;
    const created = toDate(m.createdAt);
    if (!created) return;
    const readAt = toDate(m.readAt);
    if (!readAt || (end && readAt > end)) {
      unacknowledged += 1;
      return;
    }
    const delta = readAt.getTime() - created.getTime();
    if (delta >= 0) entries.push({ ms: delta, created });
  });

  const deltas = entries.map((e) => e.ms);
  const within1h = deltas.filter((d) => d < HOUR_MS).length;
  const within15m = deltas.filter((d) => d < WITHIN_15_MIN_MS).length;
  const { byDay, byHour } = summarize(entries, dayScaffold);

  return {
    count: deltas.length,
    unacknowledged,
    medianMs: median(deltas),
    within1hPct: pct(within1h, deltas.length),
    within15mPct: pct(within15m, deltas.length),
    byDay,
    byHour,
  };
}

/**
 * Build the care-alert acknowledgement + resolution response-time section.
 * @param {Array} alertCohort  created-in-period alerts ({createdAt, acknowledgedAt, resolvedAt, status, stale})
 * @param {boolean} excludeHistorical drop stale (historical) alerts
 * @param {Array} dayScaffold continuous day list for the period
 */
function buildAlertSection(alertCohort, excludeHistorical, dayScaffold, end) {
  const cohortAll = alertCohort || [];
  const cohort = excludeHistorical ? cohortAll.filter((a) => !a.stale) : cohortAll;
  const excludedCount = cohortAll.length - cohort.length;
  const byEnd = (d) => d && (!end || d <= end);

  const ackEntries = [];
  const resolveEntries = [];
  cohort.forEach((a) => {
    const created = toDate(a.createdAt);
    if (!created) return;
    const ackd = toDate(a.acknowledgedAt);
    if (byEnd(ackd)) {
      const d = ackd.getTime() - created.getTime();
      if (d >= 0) ackEntries.push({ ms: d, created });
    }
    if (a.status === 'resolved') {
      const res = toDate(a.resolvedAt);
      if (byEnd(res)) {
        const d = res.getTime() - created.getTime();
        if (d >= 0) resolveEntries.push({ ms: d, created });
      }
    }
  });

  const ackDeltas = ackEntries.map((e) => e.ms);
  const resolveDeltas = resolveEntries.map((e) => e.ms);
  const ackWithin1h = ackDeltas.filter((d) => d < HOUR_MS).length;
  const resolveWithin24h = resolveDeltas.filter((d) => d < DAY_MS).length;
  const ack = summarize(ackEntries, dayScaffold);
  const resolve = summarize(resolveEntries, dayScaffold);

  return {
    cohortCount: cohort.length,
    excludedCount,
    ackCount: ackDeltas.length,
    ackMedianMs: median(ackDeltas),
    ackWithin1hPct: pct(ackWithin1h, ackDeltas.length),
    resolveCount: resolveDeltas.length,
    resolveMedianMs: median(resolveDeltas),
    resolveWithin24hPct: pct(resolveWithin24h, resolveDeltas.length),
    ackByDay: ack.byDay,
    ackByHour: ack.byHour,
    resolveByDay: resolve.byDay,
    resolveByHour: resolve.byHour,
  };
}

/**
 * Build the Response Time report dataset. Either section is built only when its
 * source data is supplied, so callers (e.g. the Report Package) can request just
 * the messages or just the alerts portion.
 *
 * @param {object} args
 * @param {Array}  [args.messages]    message docs for the messages section
 * @param {Set}    [args.inScopeIds]  in-scope conversationIds
 * @param {Array}  [args.alertCohort] created-in-period alerts for the alerts section
 * @param {{start: Date, end: Date}} args.range
 * @param {boolean} [args.excludeHistorical=true]
 */
export function buildResponseTimeReport({
  messages,
  inScopeIds,
  alertCohort,
  range,
  excludeHistorical = true,
} = {}) {
  const { start, end } = range || {};
  const dayScaffold = buildDayScaffold(start, end);
  return {
    messages: messages !== undefined ? buildMessageSection(messages, inScopeIds, dayScaffold, end) : null,
    alerts: alertCohort !== undefined ? buildAlertSection(alertCohort, excludeHistorical, dayScaffold, end) : null,
  };
}
